// @section 1 Delivery route - engagements + deliverables
//
// Empty/loading/populated states styled with the same inline color palette
// the rest of Atlas uses (#171c26 surface, #e6e8ee text, #6b7a90 muted,
// #232a37 border) so this route stops looking like raw HTML next to the
// other Atlas surfaces.
import { useEffect, useState } from 'react';
import { listEngagements, listDeliverables, createShareLink } from '../api';
import { useIsMobile } from '../lib/useIsMobile';

export default function Delivery() {
  const [engagements, setEngagements] = useState<any[] | null>(null);
  const [deliverables, setDeliverables] = useState<any[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // @block 1.0 Phone: own the scroll + add gutters (canvas is overflow:hidden).
  const isMobile = useIsMobile();

  useEffect(() => { listEngagements().then(setEngagements).catch(() => setEngagements([])); }, []);
  useEffect(() => {
    if (selected) listDeliverables(selected).then(setDeliverables).catch(() => setDeliverables([]));
  }, [selected]);

  const onShare = async (id: string) => {
    const link = await createShareLink(id);
    await navigator.clipboard.writeText(link.url).catch(() => {});
    alert(`Share link copied:\n${link.url}\n(expires ${new Date(link.expiresAt).toLocaleDateString()})`);
  };

  const isLoading = engagements === null;
  const isEmpty = !isLoading && engagements.length === 0;

  return (
    <div style={{
      display: 'grid', gap: 14, maxWidth: 1100,
      ...(isMobile ? { height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 14px' } : null),
    }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.1 }}>Client Deliverables</h1>
        <div style={{ fontSize: 12, color: '#6b7a90', marginTop: 4 }}>
          Pick a client engagement to browse its deliverables, copy a share link, or open the source file in Drive.
        </div>
      </div>

      {/* @block 1.1 Engagement picker - loading / empty / populated */}
      {isLoading ? (
        <div style={panelStyle}>
          <em style={mutedTextStyle}>Loading engagements...</em>
        </div>
      ) : isEmpty ? (
        <div style={panelStyle}>
          <div style={{ fontSize: 14, color: '#e6e8ee', fontWeight: 600 }}>No engagements yet</div>
          <div style={{ fontSize: 12, color: '#6b7a90', marginTop: 6, lineHeight: 1.6 }}>
            Client engagements appear here once they're created in CRM and a delivery scope is opened. Until then there are no deliverables to track.
          </div>
        </div>
      ) : (
        <div style={panelStyle}>
          <label style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: '#6b7a90', marginBottom: 6 }}>
            Engagement
          </label>
          <select
            value={selected ?? ''}
            onChange={(e) => setSelected(e.target.value || null)}
            style={selectStyle}
          >
            <option value="">Select an engagement...</option>
            {engagements.map((e: any) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      )}

      {/* @block 1.2 Deliverables for the selected engagement */}
      {selected && (
        <div style={panelStyle}>
          {deliverables === null ? (
            <em style={mutedTextStyle}>Loading deliverables...</em>
          ) : deliverables.length === 0 ? (
            <em style={mutedTextStyle}>No deliverables in this engagement yet.</em>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Due</th>
                  <th style={thStyle}>File</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {deliverables.map((d) => (
                  <tr key={d.id} style={trStyle}>
                    <td style={tdStyle}>{d.title}</td>
                    <td style={tdStyle}>{d.status}</td>
                    <td style={tdStyle}>{d.dueDate ?? d.due_date ?? '-'}</td>
                    <td style={tdStyle}>
                      {d.driveUrl
                        ? <a href={d.driveUrl} target="_blank" rel="noreferrer" style={linkStyle}>Drive</a>
                        : '-'}
                    </td>
                    <td style={tdStyle}>
                      <button onClick={() => onShare(d.id)} style={ghostBtnStyle}>Share</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// @section 2 Style tokens - mirrors the inline palette used in Pm/Strategy
const panelStyle: React.CSSProperties = {
  padding: '14px 16px',
  border: '1px solid #232a37',
  borderRadius: 8,
  background: '#171c26',
};

const mutedTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7a90',
};

const selectStyle: React.CSSProperties = {
  fontSize: 13,
  padding: '6px 10px',
  border: '1px solid #2a3346',
  borderRadius: 5,
  background: '#1d2330',
  color: '#e6e8ee',
  fontFamily: 'inherit',
  minWidth: 280,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  color: '#6b7a90',
  borderBottom: '1px solid #232a37',
  fontWeight: 600,
};

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid #1d2330',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  color: '#e6e8ee',
  verticalAlign: 'top',
};

const linkStyle: React.CSSProperties = {
  color: '#7eb6e8',
  textDecoration: 'none',
};

const ghostBtnStyle: React.CSSProperties = {
  fontSize: 11,
  padding: '4px 10px',
  border: '1px solid #2a3346',
  borderRadius: 4,
  background: 'transparent',
  color: '#a3b1c2',
  cursor: 'pointer',
  fontFamily: 'inherit',
};
